/**
 * Resolução do **preço unitário base** por `SessaoUsuario.TipoPreco` (T007).
 *
 * Devolve o preço de **uma** unidade. Quantidade e desconto não entram aqui —
 * são aplicados por `calcularTotalLinha`, que multiplica e só então subtrai
 * (`data-model.md` §5).
 *
 * Constitution III: o Checkout não reimplementa a seleção de regra de preço.
 * Para todo `TipoPreco ≠ 8`, o ERP já resolveu e entregou o resultado em
 * `PrecoVenda` (AD-059/AD-060). `8` é a única exceção, e não por escolha do
 * Checkout: a faixa depende da quantidade agregada do SKU no carrinho, estado
 * que só existe aqui — é delegação, não duplicação de fonte de verdade
 * (`research.md`, D2).
 */

import { ZERO_CENTAVOS, type Centavos } from './dinheiro';
import type { SnapshotPrecoProduto } from './linha';
import type { Milesimos } from './quantidade';

const TIPO_PRECO_MIN = 1;
const TIPO_PRECO_MAX = 11;
/** O único `TipoPreco` cujo preço é resolvido localmente, por faixa de quantidade. */
const TIPO_PRECO_POR_FAIXA = 8;

/** `TipoPreco` fora de `1..11` — configuração de sessão que o domínio não conhece. */
export class ErroTipoPrecoDesconhecido extends Error {
  constructor(tipoPreco: number) {
    super(
      `TipoPreco ${String(tipoPreco)} está fora do contrato do ERP (${String(TIPO_PRECO_MIN)}..${String(TIPO_PRECO_MAX)}).`,
    );
    this.name = 'ErroTipoPrecoDesconhecido';
  }
}

/** Produto cujo preço de tabela é zero — cadastro incompleto no ERP. */
export class ErroProdutoSemPreco extends Error {
  constructor(readonly codigoProduto: string) {
    super(`Produto ${codigoProduto} está sem preço de venda no ERP.`);
    this.name = 'ErroProdutoSemPreco';
  }
}

/** Snapshot sem o preço da faixa que a quantidade agregada atingiu. */
export class ErroFaixaSemPreco extends Error {
  constructor(codigoProduto: string, faixa: number) {
    super(
      `Produto ${codigoProduto} não tem PrecoVenda${String(faixa)} para a faixa atingida em TipoPreco 8.`,
    );
    this.name = 'ErroFaixaSemPreco';
  }
}

/**
 * Faixa (1 a 5) atingida pela quantidade agregada, em modelo de **limiar único
 * (flat)**, não progressivo: atingida a faixa, **todas** as unidades do SKU na
 * venda valem o preço dela (`FR-006`).
 *
 * Limiar `0` é faixa **não configurada** e é ignorado — o ERP devolve `0` nos
 * `QtdMinimaPreco` que o produto não usa.
 */
export function resolverFaixa(
  limiares: SnapshotPrecoProduto['limiaresFaixa'],
  quantidadeAgregada: Milesimos,
): number {
  let faixa = 1;
  // Percorre em ordem crescente e fica com a última que satisfaz, ou seja, a
  // maior faixa cujo limiar foi atingido.
  for (let indice = 0; indice < limiares.length; indice += 1) {
    const limiar = limiares[indice];
    if (limiar !== undefined && limiar > 0 && quantidadeAgregada >= limiar) {
      faixa = indice + 2;
    }
  }
  return faixa;
}

/**
 * Função **total**: `tipoPreco` fora de `1..11` lança erro de domínio explícito
 * em vez de devolver um preço silenciosamente errado.
 */
export function resolvePrecoUnitario(
  tipoPreco: number,
  snapshot: SnapshotPrecoProduto,
  quantidadeAgregada: Milesimos,
): Centavos {
  if (!Number.isInteger(tipoPreco) || tipoPreco < TIPO_PRECO_MIN || tipoPreco > TIPO_PRECO_MAX) {
    throw new ErroTipoPrecoDesconhecido(tipoPreco);
  }

  if (tipoPreco !== TIPO_PRECO_POR_FAIXA) {
    return snapshot.precoBase;
  }

  const faixa = resolverFaixa(snapshot.limiaresFaixa, quantidadeAgregada);
  const preco = snapshot.precosFaixa[faixa - 1];
  // `preco === undefined` nunca ocorre em tempo de execução — `precosFaixa` é
  // uma tupla de 5 posições sempre preenchida pelo mapper de bootstrap — mas o
  // `noUncheckedIndexedAccess` do TypeScript exige o guard.
  //
  // `faixa > 1` significa que um `QtdMinimaPreco{faixa}` foi de fato atingido
  // (`resolverFaixa` só avança além de 1 quando o limiar é `> 0` e a
  // quantidade agregada o alcança). Se o `PrecoVenda{faixa}` correspondente
  // ficou em `0`, o ERP nunca teve esse preço cadastrado: resolver em silêncio
  // para R$0,00 esconderia o erro de configuração. A faixa 1 (`PrecoVenda1`)
  // fica de fora **desta** checagem porque é o preço-base sempre aplicável, não
  // uma faixa que dependa de limiar — quem recusa o zero ali é
  // `exigirPrecoDeInsercao`, na entrada do produto. A separação é deliberada:
  // aqui a função também serve à reprecificação de linha **já inserida**, e
  // lançar sobre um cadastro que mudou no meio da venda derrubaria o recálculo
  // de um item que o operador não tem como consertar.
  if (preco === undefined || (faixa > 1 && preco === ZERO_CENTAVOS)) {
    throw new ErroFaixaSemPreco(snapshot.codigoProduto, faixa);
  }
  return preco;
}

/**
 * Recusa, **antes de existir linha**, o produto cujo preço de tabela é zero
 * (pedido do usuário, 2026-09-11).
 *
 * Preço zerado no cadastro deixou de virar linha de R$ 0,00 em silêncio. Até
 * aqui só o caminho da balança recusava, e por acidente de aritmética —
 * `quantidadePesavel` divide pelo preço para derivar o peso, então o zero
 * aparecia como `ErroPrecoIndisponivelParaPesagem`. Digitar o mesmo código, ou
 * escolhê-lo no modal, criava a linha zerada sem nenhum aviso.
 *
 * **`'E'` fica de fora porque a recusa não protegeria nada ali** (decisão do
 * usuário, 2026-09-11): em produto editável o preço da linha é o **digitado**,
 * e o operador pode alterá-lo de qualquer forma — barrar a entrada por causa do
 * cadastro só tiraria dele o caso de uso do próprio tipo, que é preço definido
 * na hora. O carrinho fica igualmente sem linha zerada porque o preço digitado
 * já é obrigado a ser maior que zero antes de o botão de inserir liberar.
 *
 * Não é o caso de supor que `'E'` venha sempre sem preço: medido no tenant
 * `c0lj6mvzeh` (2026-09-11, 195 produtos), `'E'` é o tipo dominante — 191
 * deles — e **80 têm `PrecoVenda` > 0**. Zero ali é cadastro incompleto, como
 * em qualquer outro tipo, não característica do valor.
 *
 * Roda sobre o snapshot, sem a quantidade agregada: em `TipoPreco 8` o piso é
 * `PrecoVenda1`, sempre aplicável, e as faixas acima dele continuam sendo
 * `ErroFaixaSemPreco`, que depende da quantidade e só pode ser avaliado depois.
 */
export function exigirPrecoDeInsercao(tipoPreco: number, snapshot: SnapshotPrecoProduto): void {
  if (snapshot.pesavelEditavel === 'E') {
    return;
  }

  const preco = tipoPreco === TIPO_PRECO_POR_FAIXA ? snapshot.precosFaixa[0] : snapshot.precoBase;

  if (preco === undefined || preco <= ZERO_CENTAVOS) {
    throw new ErroProdutoSemPreco(snapshot.codigoProduto);
  }
}
